import { useState, useContext, createContext, useEffect, useMemo, useRef } from "react";
import useViewport from "./useViewport";

// APIs
import api from "../services/api";
import apiAdmin from "../services/apiAdmin";
import { toast } from "react-toastify";
import moment from "moment";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { ONLINE_EVIDENCE_EVENT, resolveIsOnlineFromEvidence } from "../utils/onlineEvidence";
import { tableViewOrg } from "../utils/tableViewOrg";
import { tableViewData } from "../utils/tableViewData";
import { globalSetCookie } from "../utils/utils";
import { omitCustomNotesFromOrg } from "../utils/customNotesPersist";
import { clearLegacyTrustedDeviceCookies } from "../utils/legacyTrustedDeviceCookies";
import { clearStoredReferralData } from "../utils/patientReferralUtils";
import { maskPhone } from "../utils/loginVerify";
import { detectBrand } from "../utils/brand";
import * as icd10PendingSync from "../utils/icd10PendingSync";
import { clearAllPreventScoreWrites } from "../utils/preventScoreWriteQueue";
import { isAdminSession } from "../utils/planGate";
import { subscribe, unsubscribe, publish } from "../utils/events";
import { isServerDownError } from "../utils/serverErrors";
import { clearOfflineCaches } from "../utils/offlineCache";
import { setEncounterLocationContext, needsHomeLocation } from "../utils/encounterLocation";
import { snapshotPatientRosterIfStale } from "../utils/rosterSnapshot";
import { extensionSessionBridge, restoreRelayedSession } from "../utils/extensionSessionBridge";

export const authContext = createContext();

// Offline sessions ride the cached identity; they expire after this long
// without a successful ONLINE auth (spec: 7-day validity window).
export const OFFLINE_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// FIELD FIX (shared workstation, desktop web): cached identity alone is NOT
// enough to auto-enter an offline session. `user` + `organization` +
// `lastOnlineAuthAt` are plain identity breadcrumbs with zero credential
// material, so any server-down-class failure at startup (a DNS blip is enough)
// would silently open the LAST clinician's session — and their cached PHI — to
// whoever is standing at the machine. The spec's auto-entry posture was
// approved for the NATIVE app, where device unlock (FaceID/PIN) gates the app;
// the browser has no equivalent gate.
//
// Requiring a stored loginToken puts offline auto-entry exactly where ONLINE
// auto-entry already is: a remember-me browser, which restores that same
// identity's session unattended today (login() only persists loginToken when
// rememberLogin is set). So this path adds no unauthenticated surface that the
// browser didn't already have. A browser WITHOUT a remembered token falls
// through to the login screen — the pre-branch behavior.
//
// The native offline path does not consult this predicate (Login.js
// gotAuthToken gates on isOfflineWindowExpired + identity presence, with the
// native token held in SecureStore behind device unlock), so native offline
// login is unaffected.
export const canEnterOfflineSession = (now = Date.now()) => {
  try {
    const user = localStorage.getItem("user");
    const org = localStorage.getItem("organization");
    const rememberedToken = localStorage.getItem("loginToken");
    const stamp = Number(localStorage.getItem("lastOnlineAuthAt"));
    return (
      !!user && !!org && !!rememberedToken && Number.isFinite(stamp) && stamp > 0 && now - stamp < OFFLINE_SESSION_MAX_AGE_MS
    );
  } catch {
    return false;
  }
};

// Gate for the native gotAuthToken offline path (review finding I4).
// CONTROLLER RULING (binding, documented here + at the call site): the 7-day
// window is enforced ONLY when a lastOnlineAuthAt stamp exists. No stamp at
// all is grandfathered in (treated as NOT expired) — legacy devices upgraded
// before this stamp existed have nothing to compare against, and gating them
// would strand airplane-mode login until their first post-deploy online auth
// writes one. A garbage/non-positive stamp is treated the same way.
export const isOfflineWindowExpired = (now = Date.now()) => {
  try {
    const raw = localStorage.getItem("lastOnlineAuthAt");
    const stamp = Number(raw);
    if (!raw || !Number.isFinite(stamp) || stamp <= 0) return false;
    return now - stamp >= OFFLINE_SESSION_MAX_AGE_MS;
  } catch {
    return false;
  }
};

// Sentinel checkAuth returns when a server-down failure sent it into the
// offline STUB session instead of revalidating for real. Every other return
// path means "nothing went wrong", so callers that specifically need proof of
// a LIVE session (markServerBack, before announcing recovery) must compare
// against this rather than trusting a resolved promise.
export const CHECK_AUTH_OFFLINE_SESSION = "offline-session";

// Two recovery signals arriving inside this window are one recovery. A genuine
// second recovery that close together loses nothing: the shell re-offers its
// queue on the next wake or app launch.
const RECOVERY_ANNOUNCE_COOLDOWN_MS = 15000;

const termsOfUseVersion = {
  default: 5,
  rpg: 5,
  healcore: 5,
  shoreline: 5,
  gtm: 5,
  orders: 5,
  optimalcaregrp: 5,
  freehealthai: 5,
  alphabiologix: 5,
};

export const baaDefinitions = {
  lucas: {
    version: 1,
    title: "profile.baa.lucas",
  },
  shoreline: {
    version: 1,
    title: "profile.baa.shoreline",
  },
  meridian: {
    version: 1,
    title: "profile.baa.meridian",
  },
  gtm: {
    version: 1,
    title: "profile.baa.gtm",
  },
  optimalcaregrp: {
    version: 1,
    title: "profile.baa.optimalcaregrp",
  },
};

function useAuthProvider() {
  const { t, i18n } = useTranslation();
  const isDemoRef = useRef(false);
  // Mirrors the isAdminUser state so callbacks created before the state
  // settles (checkUserPlan, called long after login) read a live value
  // rather than a stale closure. Same pattern as isDemoRef above.
  const isAdminUserRef = useRef(false);
  const [firstCheck, setFirstCheck] = useState(true);
  const [isMobileApp] = useState(window.ReactNativeWebView !== undefined);
  const [auth, setAuth] = useState();
  const [brainId, setBrainId] = useState(localStorage.getItem("brainId"));
  const [location, setLocation] = useState();
  const [hasNotifications, setHasNotifications] = useState();
  const { isMobile } = useViewport();
  const [isDoctor, setIsDoctor] = useState();
  const [isOnline, setIsOnline] = useState(true);
  const [serverReachable, setServerReachable] = useState(true);
  const isServerAvailable = isOnline && serverReachable;
  // Backoff step for the recovery probe (30s doubling to 120s cap).
  const probeAttemptRef = useRef(0);
  // Previous device-connectivity value, so the probe effect can tell a plain
  // re-run from an actual offline→online transition.
  const wasOnlineRef = useRef(true);
  const [hostName, setHostName] = useState(localStorage.getItem("hostName"));
  const [isDebugMode, setIsDebugMode] = useState();
  const searchParams = new URLSearchParams(window.location.search);
  const [showModalUpgrade, setShowModalUpgrade] = useState();
  const [showModalChangeLocation, setShowModalChangeLocation] = useState();
  const [columnsSmartSheet, setColumnsSmartSheet] = useState([]);
  const [columnsSmartSheetKeys, setColumnsSmartSheetKeys] = useState([]);
  const [home, setHome] = useState("default");

  const [userPlan, setUserPlan] = useState();
  const [limitReached, setLimitReached] = useState();
  const [doctors, setDoctors] = useState([]);
  const [firstLogin, setFirstLogin] = useState(false);
  const [isChromeExtension, setIsChromeExtension] = useState();
  const [isChromeExtensionInstalled, setIsChromeExtensionInstalled] = useState();
  const [isAdminUser, setIsAdminUser] = useState();

  // Layout states
  const [showLeftSidebar, setShowLeftSidebar] = useState();
  const [iconSidebar, setIconSidebar] = useState();

  const getBrand = () => detectBrand(window.location.host, searchParams.get("brand"), sessionStorage.getItem("brand"));

  const [brand] = useState(getBrand());
  const isOrdersBrand =
    brand === "shoreline" ||
    brand === "gtm" ||
    brand === "orders" ||
    brand === "optimalcaregrp" ||
    brand === "freehealthai" ||
    brand === "alphabiologix";

  useEffect(() => {
    try {
      setIsChromeExtension(!!window.parent[0]?.chrome);
    } catch (e) {}

    if (!sessionStorage.getItem("brand") && brand) {
      sessionStorage.setItem("brand", brand);
    }

    clearLegacyTrustedDeviceCookies();

    // eslint-disable-next-line
  }, []);

  // Offline mode (spec §1): any API call that fails with a server-down-class
  // error announces "serverDown"; we flip serverReachable and start probing.
  useEffect(() => {
    const onServerDown = () => {
      setServerReachable((prev) => {
        if (prev) {
          console.warn("Server unreachable — entering server-outage mode");
          Sentry.addBreadcrumb({ category: "offline", message: "server-unreachable", level: "warning" });
        }
        return false;
      });
    };
    subscribe("serverDown", onServerDown);
    return () => unsubscribe("serverDown", onServerDown);
  }, []);

  // Bring a session back to life after an outage. Extracted from markServerBack
  // so BOTH recovery flavors run the identical revalidation instead of one of
  // them announcing on a session nobody checked.
  //
  // `force`: the current session may itself be an offline stub, which checkAuth
  // would otherwise decline to revalidate. A resolved promise is NOT proof of a
  // live session — during a flap, checkAuth can fail server-down and quietly
  // re-enter an offline stub, returning the sentinel instead of throwing.
  // Announcing recovery on that would tell every listener a live session exists
  // while the server is still down (and can burn finalless-recording give-up
  // strikes).
  const revalidateSession = async () => {
    try {
      const result = await checkAuth(false, undefined, { force: true });
      return result !== CHECK_AUTH_OFFLINE_SESSION;
    } catch (e) {
      const storedToken = localStorage.getItem("loginToken");
      if (storedToken && !isServerDownError(e)) {
        // login() never throws — its own catch swallows every failure and
        // RETURNS the error (or an offline-session marker with no sessionId)
        // instead (review finding I2). A truthy sessionId is the only signal a
        // live session actually came back; anything else — including the
        // {offline:true} shape login() returns when it falls back to a
        // cached-identity offline session — must not count as revalidated. On
        // failure the Login screen takes over; queued work stays queued, and the
        // downstream hooks' own auth-driven effects pick it back up on the next
        // successful login (not this publish).
        const r = await login(
          localStorage.getItem("user"),
          undefined,
          localStorage.getItem("organization"),
          undefined,
          storedToken,
          true
        );
        return !!r?.sessionId;
      }
      // Server-down-class failure: checkAuth's own catch already flipped
      // serverReachable back to false and the probe effect re-arms on that state
      // change — nothing further to do except skip the announcement.
      return false;
    }
  };

  // THE single recovery announcement (spec §4). There used to be two
  // mechanisms — this event for the server-outage flavor, and a
  // `cameOnline && serverReachable` edge in usePostMessages for the
  // connectivity flavor — claimed to be mutually exclusive by construction.
  // They were not:
  //
  //   * markServerBack sets serverReachable TRUE before it awaits the
  //     revalidation, so a radio blip inside that window made the consumer's
  //     edge fire too, and the announcement then fired again.
  //   * the probe timer is independent of isOnline, so on airplane-mode exit it
  //     could complete BEFORE the shell posted onConnectionStatusChange — again
  //     both.
  //
  // Either way the shell was asked twice for one queue and replayed every diff
  // in it twice, which with no create-side idempotence filed duplicate
  // encounters and duplicate patients. So: one publisher, both flavors, latched.
  // An in-flight announcement swallows every concurrent request and a cooldown
  // swallows every near-simultaneous one, so there is no partition left to be
  // wrong about.
  //
  // Deliberately does NOT queue a trailing run: revalidateSession can take
  // seconds, and losing a coalesced wake (the shell re-offers on the next wake
  // or launch) is strictly better than a double delivery.
  const recoveryAnnounceRef = useRef({ inFlight: false, lastAt: 0 });
  const announceRecovered = async (reason) => {
    const state = recoveryAnnounceRef.current;
    if (state.inFlight) return;
    if (Date.now() - state.lastAt < RECOVERY_ANNOUNCE_COOLDOWN_MS) return;
    state.inFlight = true;
    try {
      // Revalidate BEFORE announcing recovery so listeners (sync replay,
      // recording drain, list refresh) run against a live session.
      const revalidated = await revalidateSession();
      if (!revalidated) return;
      state.lastAt = Date.now();
      // auth state may be stale in this closure; read identity from
      // localStorage (the snapshot's own scope guard makes a missing identity a
      // no-op).
      snapshotPatientRosterIfStale({
        userId: localStorage.getItem("authUserId"),
        orgId: localStorage.getItem("organization"),
      });
      publish("serverBackOnline", { reason });
    } finally {
      state.inFlight = false;
    }
  };

  // Recovery probe: while the server is unreachable, retry a lightweight call
  // on a 30s→120s backoff. ANY response (even 401/500) proves the server is
  // back — only server-down-class failures keep us in outage mode.
  useEffect(() => {
    const cameOnline = !wasOnlineRef.current && isOnline;
    wasOnlineRef.current = isOnline;
    if (serverReachable) {
      probeAttemptRef.current = 0;
      // CONNECTIVITY flavor: the radio was off (airplane mode, a tunnel) and no
      // request was ever attempted, so serverReachable never went false and no
      // probe ever armed. Nothing used to wake the deferred replay batch until
      // the next app launch — and useChats' list refresh and useIndexDb's
      // recording drain, which only listen for serverBackOnline, got no wake at
      // all. It costs one checkAuth round trip that the old consumer-side edge
      // did not pay; that is the price of the guarantee that a replay never runs
      // against a dead session.
      if (cameOnline) announceRecovered("connectivity-restored");
      return;
    }
    let cancelled = false;
    let timer;
    const probe = async () => {
      try {
        await api.getUserProfile();
        if (!cancelled) markServerBack();
      } catch (e) {
        if (cancelled) return;
        if (!isServerDownError(e)) {
          markServerBack(); // the server answered — it's an app/auth error, not an outage
          return;
        }
        probeAttemptRef.current += 1;
        schedule();
      }
    };
    const schedule = (delayMs) => {
      const delay = typeof delayMs === "number" ? delayMs : Math.min(120000, 30000 * 2 ** probeAttemptRef.current);
      timer = setTimeout(probe, delay);
    };
    const markServerBack = async () => {
      probeAttemptRef.current = 0;
      Sentry.addBreadcrumb({ category: "offline", message: "server-recovered", level: "info" });
      setServerReachable(true);
      await announceRecovered("server-recovered");
    };
    // A device-connectivity blip (tunnel, elevator, wifi handoff) fails every
    // in-flight request server-down-class, so serverReachable can be false
    // purely because the radio was off. Once the device is back the accrued
    // backoff — up to 120s — is punishing the wrong thing: reset it and probe
    // straight away instead of leaving the app in outage mode for two minutes
    // after connectivity returns.
    if (cameOnline) probeAttemptRef.current = 0;
    schedule(cameOnline ? 0 : undefined);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [serverReachable, isOnline]);

  // Correct a stuck-offline belief from evidence. `isOnline` is fed by the native
  // shell forwarding NetInfo's tri-state `isInternetReachable`, and on a network
  // that blackholes NetInfo's probe it can sit at "offline"/"unknown" for a whole
  // session while our own requests succeed — NetInfo only emits on CHANGE, so
  // nothing ever corrects it. Since `isOnline` gates the transcript save, that
  // silently defers every recording (see utils/onlineEvidence).
  //
  // MOBILE ONLY, deliberately: on web nothing feeds this state except the
  // dev-settings offline toggle, whose entire purpose is to simulate offline while
  // requests still succeed. Recovering there would make that tool unusable, and the
  // bug does not exist there.
  useEffect(() => {
    if (!isMobileApp) return undefined;

    const onEvidence = () => setIsOnline(resolveIsOnlineFromEvidence);
    subscribe(ONLINE_EVIDENCE_EVENT, onEvidence);
    return () => unsubscribe(ONLINE_EVIDENCE_EVENT, onEvidence);
  }, [isMobileApp]);

  const loginOffline = async (user, org) => {
    setAuth({
      offline: true,
      session: {
        currentOrganizationName: org,
      },
      user: {
        // Cache scope (buildScope) keys on user.id, not email — without it
        // every offline cache read/write scopes to "" and misses (review
        // finding C1). authUserId is written by the last successful ONLINE
        // login for exactly this identity purpose.
        id: localStorage.getItem("authUserId") || undefined,
        email: user,
        custom: {},
      },
    });
    // Defensive: callers reach here from a native handoff whose `org` may be
    // missing. Never persist "undefined"/"" over a good stored value — the
    // stored org is what the NEXT launch scopes its cache to.
    if (typeof org === "string" && org.trim()) localStorage.setItem("organization", org);
  };

  // `signOutOtherSessions`: the user chose to sign out their other devices after a concurrent-session refusal. Only
  // the Login page passes it, on an explicit click; automatic re-logins never do.
  const login = async (user, password, organization, verificationCode, loginToken, rememberLogin, mode, signOutOtherSessions) => {
    let response;
    try {
      let agent = "mobile-native";
      if (brand === "rpg" || brand === "healcore") {
        agent = "embedded";
      }

      // A clinical-profile activation may be opened in a fresh browser with no
      // saved tenant state. Login.js persists the allowlisted server from the
      // activation callback so the first login reaches the same backend that
      // created the account. api.login rejects unknown values fail-closed.
      const hasClinicalProfileClaim =
        !!localStorage.getItem("cpInviteToken") &&
        !!localStorage.getItem("cpProfileId") &&
        !!localStorage.getItem("cpProviderOrg");
      const cpServer = hasClinicalProfileClaim ? sessionStorage.getItem("cpActivationServer") || undefined : undefined;
      response = await api.login(
        user,
        password,
        organization,
        verificationCode,
        agent,
        true,
        loginToken,
        mode,
        cpServer,
        signOutOtherSessions
      );
      if (response.status === 200) {
        let auth = response.data;
        if (auth.requireVerification) {
          console.log("Request verification token");
          return auth;
        } else {
          // Redirect to specific brand subdomain if applicable
          if (process.env.REACT_APP_ENV === "production" || process.env.REACT_APP_ENV === "staging") {
            let prefix = process.env.REACT_APP_ENV === "staging" ? "staging-" : "";
            const brands = ["shoreline", "gtm", "rpg", "healcore", "optimalcaregrp", "freehealthai"];
            for (let brand of brands) {
              if (response.data.hostName === brand + ".lucashealth.ai") {
                if (!window.location.hostname.includes(brand)) {
                  let autologin = {
                    user,
                    password,
                    organization,
                    verificationCode,
                    loginToken,
                    rememberLogin,
                    mode,
                  };

                  globalSetCookie("autologin", JSON.stringify(autologin), 1);
                  window.location.href = "https://" + prefix + brand + "-app.lucashealth.ai/";
                  await new Promise(() => {}); // Wait indefinitely for redirect
                }
              }
            }

            if (response.data.currentOrganizationName === "master-orders") {
              // TODO:
            }
          }

          // Set hostname
          let hostName = getBaseURL();
          if (response.data.hostName) {
            hostName = "https://" + response.data.hostName;
          }

          localStorage.setItem("hostName", hostName);
          api.api.defaults.baseURL = hostName;
          setHostName(hostName);

          // Login valid
          sessionStorage.removeItem("sessionId");
          localStorage.removeItem("sessionId");
          if (rememberLogin) {
            localStorage.setItem("loginToken", auth.loginToken);
            localStorage.setItem("sessionId", auth.sessionId);
          } else {
            sessionStorage.setItem("sessionId", auth.sessionId);
          }

          // Hand the fresh breadcrumb to the extension side panel so reopening the
          // panel does not read as a logout. Inert everywhere else: with no
          // validated handshake the bridge holds no reply origin and sends nothing.
          //
          // Deliberately only here. Impersonation (utils/impersonation.js) writes
          // sessionStorage directly and never comes through login(), so an
          // operator's "enter as user" session is never parked in the panel's store.
          extensionSessionBridge.sendSessionUpdate(auth.sessionId);

          localStorage.setItem("brainId", auth.currentBrainId);
          localStorage.setItem("organization", auth.currentOrganizationName?.toLowerCase());
          localStorage.setItem("user", auth.userEmail);
          if (auth.user?.id) localStorage.setItem("authUserId", auth.user.id);

          setBrainId(auth.currentBrainId);
          await checkAuth(false, auth.sessionId);

          return auth;
        }
      } else {
        logout();
      }
    } catch (e) {
      console.warn("Login not allowed with " + (loginToken ? "login token" : "credentials"));
      console.warn(e.toString());

      // Server outage ≠ bad credentials (spec §1). A server-down-class failure
      // must never destroy the cached token — that lockout was the root cause
      // of the 2026-08 outage. Enter an offline session when permitted.
      if (isServerDownError(e)) {
        setServerReachable(false);
        if (canEnterOfflineSession()) {
          console.warn("Server unreachable during login — entering offline session");
          await loginOffline(localStorage.getItem("user"), localStorage.getItem("organization"));
          return { offline: true };
        }
        return e; // unreachable AND no cached identity — nothing to enter
      }

      logout();
      return e;
    }
  };

  const logout = async () => {
    setAuth(null);
    setHome("default");
    // Drop the Sentry identity synchronously. Otherwise every event between this
    // logout and the next successful checkAuth — including the login screen's own
    // errors — is still attributed to the org that just signed out.
    Sentry.setUser(null);
    Sentry.setTag("organization", undefined);
    Sentry.setTag("organization_plan", undefined);
    // Clinical background work must lose ownership synchronously. The network
    // logout can be slow or fail, and must never leave PHI-bearing closures
    // available to replay under a later session on this browser.
    icd10PendingSync.clearAll();
    clearAllPreventScoreWrites();
    // The side panel's copy of the breadcrumb is part of this purge, and it must go
    // in the synchronous prefix ABOVE the awaited network logout: if the panel
    // document is closed while api.logout() is in flight, the request is cancelled —
    // the server session and cookie stay alive — and anything after the await never
    // runs. A surviving panel breadcrumb would then sign the next person at the
    // machine straight back into this session on reopen. No-op outside the panel.
    extensionSessionBridge.sendSessionClear();
    try {
      await api.logout();
    } catch (e) {}
    sessionStorage.removeItem("sessionId");
    // Clear the resolved brand so a stale/junk value can't bleed into the next
    // user on a shared browser (they'd inherit the wrong brand — and a junk one
    // would loop their onboarding). detectBrand also rejects unknown values on
    // read, but clearing here keeps sessionStorage clean for raw readers too.
    sessionStorage.removeItem("brand");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("loginToken");
    localStorage.removeItem("authUserId");
    localStorage.removeItem("brainId");
    setBrainId(null);
    localStorage.removeItem("hostName");
    setHostName(null);
    localStorage.removeItem("lastOnlineAuthAt");
    // Explicit logout purges the offline PHI caches (patients/chats). The
    // durable recording queue is intentionally NOT touched — never abandon
    // audio (useIndexDb's guarantee).
    //
    // AWAITED. Fire-and-forget left the purge racing the rest of logout on a
    // shared device: it bumps the cache epoch synchronously (which is what
    // invalidates any in-flight persist), but the actual delete must also be
    // done before this function returns and the next user can sign in.
    // clearOfflineCaches never rejects, so this cannot block the sign-out.
    await clearOfflineCaches();
    localStorage.removeItem("pendingWizardProfile");
    localStorage.removeItem("pendingWizardOrgInfo");
    localStorage.removeItem("cpInviteToken");
    localStorage.removeItem("cpProfileId");
    localStorage.removeItem("cpProviderOrg");
    sessionStorage.removeItem("cpActivationServer");
    Cookies.remove("sessionId");
    Cookies.remove("brain");
    Cookies.remove("organizationName");
    clearStoredReferralData();
    // Dev/staging only: any active dev-preview override must die with the
    // session. Without this, a tester logging out of one account and into
    // another would inherit the previous session's preview flag, which
    // would mislead QA. The gate at devPreview.js means production is
    // already inert; this is a staging-correctness fix.
    try {
      sessionStorage.removeItem("__devPreview");
    } catch (e) {
      /* private mode / quota */
    }

    try {
      if (window?.ReactNativeWebView) {
        window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: "onLogout" }));
      }
    } catch (e) {
      console.error("Error sending logout message to mobile app");
      console.error(e);
    }
  };

  // `force` (recovery only): revalidate even though the current session is an
  // offline stub. Without it the guard below short-circuits and the stub auth
  // — no orgInfo, no features — survives until a manual reload even after the
  // server comes back. Device connectivity (`!isOnline`) still short-circuits:
  // there is nothing to talk to.
  const checkAuth = async (firstLogin, loginSessionId, { force = false } = {}) => {
    // Skip during demo mode — prevents real API calls from overwriting fake auth
    if (isDemoRef.current) return true;
    if ((auth && auth.offline && !force) || !isOnline) return true;

    if (firstLogin) {
      setFirstLogin(true);
    }

    let sessionId = sessionStorage.getItem("sessionId") || localStorage.getItem("sessionId") || loginSessionId;
    if (sessionId) {
      let response;
      try {
        response = await api.getUserProfile();
        setFirstLogin(false);

        if (response.status === 200 && response.data) {
          let authData = response.data;

          // Fetch org info
          console.log("Fetching organization info");
          let orgInfo = await api.getOrgInfo();
          response.data.orgInfo = orgInfo.data?.result?.[0] || {};

          // Merge pending wizard profile into auth data so the wizard guard
          // doesn't re-show the wizard when the profile hasn't synced yet
          const pendingProfileRaw = localStorage.getItem("pendingWizardProfile");
          let pendingProfileData = null;
          if (pendingProfileRaw) {
            try {
              pendingProfileData = JSON.parse(pendingProfileRaw);
              response.data.user = {
                ...response.data.user,
                ...pendingProfileData,
                custom: { ...response.data.user.custom, ...pendingProfileData.custom },
              };
            } catch (e) {
              console.warn("Failed to parse pending wizard profile:", e.message);
            }
          }

          setAuth(response.data);

          // Stamp the moment of the last successful ONLINE auth; offline
          // sessions are honored for OFFLINE_SESSION_MAX_AGE_MS from here.
          try {
            localStorage.setItem("lastOnlineAuthAt", String(Date.now()));
          } catch (_e) {}

          // Identity, not credential — consistent with the existing user/
          // organization keys. Read by the recovery handler's closure to
          // avoid stale-state re-snapshots after a server outage.
          if (authData.user?.id) localStorage.setItem("authUserId", authData.user.id);

          // Refresh the offline patient roster (24h-throttled, fire-and-forget).
          snapshotPatientRosterIfStale({
            userId: authData.user?.id,
            orgId: authData.session?.currentOrganizationName,
          });

          // Mirror MFA preference to localStorage so the verify modal can show
          // the correct channel (email vs SMS) on the FIRST send of the NEXT
          // login — before the server has confirmed the channel. Only a masked
          // phone is stored; the full number is never persisted here.
          try {
            const emailKey = authData.user.email?.trim()?.toLowerCase();
            if (emailKey) {
              const mfaPref = authData.user.mfaPreference;
              if (mfaPref === "email" || mfaPref === "sms") {
                localStorage.setItem("mfaChannel_" + emailKey, mfaPref);
              }
              // Cache the masked phone (last 2 digits only) for every user so the
              // SMS state can show the number whenever the channel resolves to SMS
              // — including an email-preference user who falls back to "Resend via
              // text". Stored only when a phone exists; the full number never is.
              const maskedPhone = maskPhone(authData.user.mobilePhone);
              if (maskedPhone) {
                localStorage.setItem("mfaMaskedPhone_" + emailKey, maskedPhone);
              }
            }
          } catch (_e) {
            // localStorage unavailable (private browsing mode) — not critical
          }

          console.log("Login success");
          Sentry.setUser({
            email: authData.user.email,
            id: authData.user.id,
            organization: authData.session.currentOrganizationName,
          });
          // The organization ALSO has to be a tag, not only a user field: Sentry
          // only indexes id/email/username/ip_address on the user object, so any
          // other key there renders as unsearchable additional data. Tags are
          // indexed — this is what makes "organization:acme" filterable in issue search,
          // usable in alert conditions, and visible in the issue tag breakdown.
          // Tags are also outside the beforeSend user scrubber's allowlist path,
          // so they survive regardless of how that scrubber evolves.
          Sentry.setTag("organization", authData.session.currentOrganizationName || "unknown");
          Sentry.setTag("organization_plan", authData.session.currentOrganizationPlan || "unknown");
          setIsDoctor(authData.user.groups.indexOf("doctor") !== -1);
          const isAdmin = isAdminSession(authData.user);
          isAdminUserRef.current = isAdmin;
          setIsAdminUser(isAdmin);
          checkLocation(authData.user);

          apiAdmin.api.interceptors.request.use(function (config) {
            config.headers["host-name"] = localStorage.getItem("hostName");
            return config;
          });

          // Fetch doctors
          fetchDoctors();

          // Retry syncing pending wizard data to the server
          if (pendingProfileData) {
            try {
              await api.updateProfile(pendingProfileData);
              localStorage.removeItem("pendingWizardProfile");
              console.log("Pending wizard profile synced successfully");
            } catch (e) {
              console.warn("Failed to sync pending wizard profile, will retry next login:", e.message);
            }
          }
          const pendingOrgInfoRaw = localStorage.getItem("pendingWizardOrgInfo");
          if (pendingOrgInfoRaw) {
            try {
              await api.updateOrgInfo(JSON.parse(pendingOrgInfoRaw));
              localStorage.removeItem("pendingWizardOrgInfo");
              console.log("Pending wizard org info synced successfully");
            } catch (e) {
              console.warn("Failed to sync pending wizard org info, will retry next login:", e.message);
            }
          }

          // Clinical Profile bootstrap — first-login claim of a provider-created profile.
          // Idempotent on the server; we still clear keys on success so we don't re-fire.
          const cpInviteToken = localStorage.getItem("cpInviteToken");
          const cpProfileId = localStorage.getItem("cpProfileId");
          const cpProviderOrg = localStorage.getItem("cpProviderOrg");
          if (
            cpInviteToken &&
            cpProfileId &&
            cpProviderOrg &&
            response.data.session?.currentOrganizationPlan === "order-patient"
          ) {
            // T24-1 Fix A: concurrent-tab guard. The bootstrap endpoint
            // single-use-consumes the invite token, so if two tabs login
            // simultaneously both will read the token from localStorage and
            // POST. Tab A succeeds; Tab B hits a now-consumed token and
            // pops a scary "invite no longer valid" toast even though the
            // profile IS linked. Use a TTL-aware in-flight flag so the
            // second tab skips bootstrap. TTL handles the case where a
            // tab is killed mid-flight (process crash, hard refresh) and
            // never reaches the finally block — a stale flag would
            // otherwise permanently block bootstrap.
            const BOOTSTRAP_INFLIGHT_TTL_MS = 30 * 1000;
            const inFlightRaw = localStorage.getItem("cpBootstrapInFlight");
            const inFlightTs = inFlightRaw ? Number(inFlightRaw) : 0;
            const inFlightFresh =
              Number.isFinite(inFlightTs) && inFlightTs > 0 && Date.now() - inFlightTs < BOOTSTRAP_INFLIGHT_TTL_MS;
            if (inFlightFresh) {
              console.log("Clinical profile bootstrap skipped: another tab is handling it");
            } else {
              localStorage.setItem("cpBootstrapInFlight", String(Date.now()));
              try {
                await api.bootstrapMyClinicalProfile({
                  providerOrg: cpProviderOrg,
                  profileId: cpProfileId,
                  inviteToken: cpInviteToken,
                });
                localStorage.removeItem("cpInviteToken");
                localStorage.removeItem("cpProfileId");
                localStorage.removeItem("cpProviderOrg");
                sessionStorage.removeItem("cpActivationServer");

                // T4 mitigation: log a stable status string only. The api.js
                // interceptor appends the response body to error messages, so
                // we never log e.message — only HTTP status codes for debugging.
                console.log("Clinical profile bootstrapped successfully");

                // Review fix #4: refresh auth so components reading
                // user.custom.clinicalProfile see the freshly-linked profile
                // without requiring a page reload. Mirrors the existing
                // pendingWizardProfile retry pattern above. orgInfo is
                // preserved because getUserProfile does not return it.
                // Shallow-clone so we don't mutate the axios response object.
                try {
                  const refreshed = await api.getUserProfile();
                  if (refreshed.status === 200 && refreshed.data) {
                    setAuth((prev) => ({
                      ...refreshed.data,
                      orgInfo: prev?.orgInfo ?? refreshed.data.orgInfo,
                    }));
                  }
                } catch (refreshErr) {
                  // T4: log status only. e.cause is the raw axios error;
                  // .response is the HTTP response object.
                  console.warn("Bootstrap succeeded but auth refresh failed, status:", refreshErr?.cause?.response?.status);
                }
              } catch (e) {
                // Review fix #2: bootstrap failure UX. A "hard fail" (invalid
                // token / profile gone / token expired) means looping on every
                // login won't help — show a user-facing toast and clear keys
                // so the patient knows to ask their provider for a new link.
                // Soft fails (network blip) are silent and retry next login.
                const detail = e?.cause?.response?.data?.detail || "";
                const d = detail.toLowerCase();
                const status = e?.cause?.response?.status;
                // T24-1 Fix B: status-code first, substring fallback.
                // Pure substring matching is fragile — if backend message
                // text drifts ("invite token expired", "invite no longer
                // valid") the FE silently downgrades to soft-fail and
                // retries forever on every login. Status codes are the
                // contract; substring stays as belt-and-suspenders for
                // older backend deploys that didn't yet return clean codes.
                // TODO(backend): coordinate with backend to return
                // structured errorCode (e.g. INVITE_INVALID) so we can
                // drop the fragile substring matching. Until then, status
                // code + substring is the belt-and-suspenders.
                const isHardFail =
                  status === 404 ||
                  status === 410 ||
                  status === 401 ||
                  d.includes("invalid invite") ||
                  d.includes("profile not found") ||
                  d.includes("invite token expired") ||
                  d.includes("invite no longer valid");
                if (isHardFail) {
                  // T24-1 Fix C: defense-in-depth against the lost-race
                  // window. Even with Fix A, if Tab A finishes (and clears
                  // the in-flight flag) before Tab B even reads it, Tab B
                  // will still POST and hard-fail. Check if the profile is
                  // now populated on auth — if so, the failure is a benign
                  // race-loser (sibling tab already linked the profile),
                  // not a real invalid-token. Suppress the scary toast but
                  // still clear the cp* keys so we don't retry next login.
                  const profileLinked = !!auth?.user?.custom?.clinicalProfile;
                  if (profileLinked) {
                    console.log(
                      "Clinical profile bootstrap hard-fail suppressed: profile already linked by sibling tab, status:",
                      status
                    );
                  } else {
                    toast.error(
                      t(
                        "pages.bootstrap.inviteInvalid",
                        "Your clinical profile invite is no longer valid. Please ask your provider for a new link."
                      )
                    );
                    // T4 + observability: status code only. Elevated to
                    // console.error so Sentry's captureConsoleIntegration({
                    // levels: "error" }) picks it up — support needs visibility
                    // on hard-fails (token dead, profile gone) for triage.
                    console.error("Clinical profile bootstrap hard-fail, status:", status);
                  }
                  localStorage.removeItem("cpInviteToken");
                  localStorage.removeItem("cpProfileId");
                  localStorage.removeItem("cpProviderOrg");
                  sessionStorage.removeItem("cpActivationServer");
                } else {
                  // Soft fails (network blip) retry next login — warn-level
                  // only, no Sentry capture needed.
                  console.warn("Failed to bootstrap clinical profile, status:", status);
                }
              } finally {
                // T24-1 Fix A: always clear the in-flight flag, regardless
                // of success/failure, so the next login attempt isn't
                // blocked by a stale flag (TTL is the safety net for
                // process crashes; this is the happy-path cleanup).
                localStorage.removeItem("cpBootstrapInFlight");
              }
            }
          }

          // Check note generation limit, trial expiry and billing blocks. The backend User Plan decides for every
          // plan (ultimate, and an org paid outside Stripe, are only blocked by an admin's block date). Admin sessions
          // still load the plan, so support sees the billing banner, but are never blocked by the modal: they must
          // be able to work inside a capped or expired customer org.
          try {
            let userPlan = await api.getUserPlan();
            const plan = userPlan.data.output;
            setUserPlan({ ...plan, organizationName: response.data.session.currentOrganizationName });
            // Reset on every login: a block from the previous organization must not carry over to this one.
            setLimitReached(false);
            if (!isAdminSession(authData.user) && isPlanExhausted(plan)) {
              console.warn("Plan blocked: notes=%s/%s lapsed=%s", plan.totalNotes, plan.maxNotes, plan.subscriptionLapsed);
              setShowModalUpgrade(true);
              setLimitReached(true);
            }
          } catch (e) {
            console.warn("Could not fetch user plan:", e.message);
          }

          // Check org status
          let status = await api.getOrgStatus();
          if (status.data) {
            for (let issue of status.data?.result?.data) {
              if (issue.ended == null) {
                if (issue.updates != null && issue.updates.length > 0) {
                  let message = issue.updates[issue.updates.length - 1].message;
                  const deltaLastUpdate = moment.duration(Date.now() - moment(issue.updated).valueOf()).humanize();
                  toast.info(deltaLastUpdate + " - " + message);
                }
              }
            }
          }

          // Check tickets notifications
          if (process.env.REACT_APP_ENDPOINT_ADMIN) {
            try {
              let tickets = await apiAdmin.getTickets();
              let toRead = Array.isArray(tickets.data) && tickets.data.find((t) => t.toRead);
              setHasNotifications(!!toRead);
            } catch (e) {
              console.error("Cannot read notifications", e);
            }
          }
        }
      } catch (error) {
        // Server unreachable ≠ session invalid (spec §1). Keep credentials and
        // enter an offline session when permitted; the recovery probe will
        // revalidate for real once the server is back.
        if (isServerDownError(error)) {
          setServerReachable(false);
          if (canEnterOfflineSession()) {
            console.warn("Server unreachable during checkAuth — entering offline session");
            await loginOffline(localStorage.getItem("user"), localStorage.getItem("organization"));
            // Offline entry must not strand the first-login spinner state (Login.js loops on firstLogin=true).
            setFirstLogin(false);
            // Truthy (legacy callers only checked for a resolved promise) but
            // distinguishable: this is a STUB session, not a revalidated one.
            return CHECK_AUTH_OFFLINE_SESSION;
          }
        }
        console.warn("Session id not valid");
        console.warn(error.toString());
        // Session expired
        clearAllPreventScoreWrites();
        setAuth(null);
        setFirstLogin(false);

        throw error;
      }
    }
  };

  const checkLocation = (user) => {
    let currentLocation = user.custom?.actualLocationId || user.currentLocationId;
    if (user.locations) {
      if (user.locations.length === 1) {
        // If has only one location, use it
        changeLocation(user.locations[0].id, user.id);
      } else if (currentLocation) {
        // If has a preferred location, use it
        let currentLocUser = user.locations.find((l) => l.id === currentLocation);
        if (currentLocUser) {
          // Valid location
          let lastSavedLocation = localStorage.getItem("location_" + user?.id);
          if (user?.custom.actualLocationId !== lastSavedLocation && lastSavedLocation !== null) {
            changeLocation(lastSavedLocation || currentLocUser.id, user.id);

            let lastOnThisDevice = user.locations.find((l) => l.id === lastSavedLocation);
            setShowModalChangeLocation({ device: lastOnThisDevice?.name, account: currentLocUser?.name });
          } else {
            changeLocation(currentLocation, user.id);
          }
        } else {
          // Invalid location
          changeLocation("", user.id);
        }
      } else {
        // No preferred location, multiple locations available
        changeLocation("", user.id);
      }

      // An empty location is a legitimate VIEW ("All locations"), but it is not
      // something a new encounter can be filed against. If we got here with no
      // durable home location to fall back on, every creation path in the app is
      // one click away from producing an encounter no location-scoped list can
      // see — so ask once, now, instead of failing later at an arbitrary moment.
      if (needsHomeLocation(user)) {
        setShowModalChangeLocation({ requireLocation: true });
      }
    }
  };

  useEffect(() => {
    if (auth?.user?.language) {
      i18n.changeLanguage(auth.user.language);
      localStorage.setItem("userLanguage", auth?.user?.language);
    }
    // eslint-disable-next-line
  }, [auth?.user?.language]);

  const changeLocation = (locationId, userId) => {
    setLocation(locationId);
    if (isDemoRef.current) return; // Skip real API calls and localStorage writes in demo mode

    // The view filter and the home location are two different things. Picking
    // "All locations" sets the filter to "" — and this function used to write
    // that "" straight over the saved preference, so choosing the All view
    // silently destroyed the only value encounter creation could fall back on.
    // Only a real location updates `homeLocationId`; the filter fields keep
    // their existing behaviour so the list scoping is unchanged.
    const profileUpdate = { currentLocationId: locationId, custom: { actualLocationId: locationId } };
    if (locationId) profileUpdate.custom.homeLocationId = locationId;

    api.updateProfile(profileUpdate);

    if (locationId) {
      // Mirror it locally so encounter creation resolves against the new home
      // immediately, without waiting for a profile refetch.
      setAuth((prev) =>
        prev?.user ? { ...prev, user: { ...prev.user, custom: { ...prev.user.custom, homeLocationId: locationId } } } : prev
      );
    }

    if (userId || auth?.user?.id) {
      localStorage.setItem("location_" + (userId || auth.user.id), locationId);
    }
  };

  // api.createChat is the one funnel every encounter-creation path goes through,
  // but it is a plain module with no access to React context. Publish the user
  // and the current view filter so its location backstop can resolve against
  // live values instead of a stale snapshot.
  useEffect(() => {
    setEncounterLocationContext({ user: auth?.user, filterLocation: location });
  }, [auth?.user, location]);

  const updateProfile = async (data) => {
    await api.updateProfile(data);
    let response = await api.getUserProfile();
    if (response.status === 200 && response.data) {
      setAuth((auth) => {
        let newAuth = response.data;
        newAuth.orgInfo = auth.orgInfo;
        return newAuth;
      });
    }
    return response;
  };

  const updateOrgInfo = async (updatedOrgInfo) => {
    await api.updateOrgInfo(updatedOrgInfo);
    setAuth((prev) => {
      // api.updateOrgInfo never sends customNotes (the server kept its copy), so
      // keep ours too rather than adopting the caller's possibly-stale snapshot.
      const kept = prev?.orgInfo?.customNotes;
      const nextOrg = omitCustomNotesFromOrg(updatedOrgInfo);
      return {
        ...prev,
        orgInfo: kept !== undefined ? { ...nextOrg, customNotes: kept } : nextOrg,
      };
    });
  };

  // Sole writers of custom note templates. Every other save of the user or org
  // record has customNotes stripped at the API layer (utils/customNotesPersist),
  // so a stale snapshot from another tab, the extension panel or the phone can
  // never wipe templates built elsewhere.
  const updateOrgCustomNotes = async (customNotes) => {
    await api.updateOrgCustomNotes(customNotes);
    setAuth((prev) => ({ ...prev, orgInfo: { ...(prev?.orgInfo || {}), customNotes } }));
  };

  const updateUserCustomNotes = async ({ customNotes, availableNotes } = {}) => {
    await api.updateProfileCustomNotes({ customNotes, availableNotes });
    setAuth((prev) => {
      if (!prev?.user) return prev;
      const custom = { ...(prev.user.custom || {}) };
      if (customNotes !== undefined) custom.customNotes = customNotes;
      if (availableNotes !== undefined) custom.availableNotes = availableNotes;
      return { ...prev, user: { ...prev.user, custom } };
    });
  };

  // Re-pull the org's DoctorOffice vertex and merge it into auth so orgInfo-derived
  // UI (the Settings "Received Contracts" badge, the Layout PSA reminder) reflects a
  // change made elsewhere this session — e.g. after signing a received contract —
  // without a full page reload. Composes the two patterns that already exist: the
  // login read (checkAuth: getOrgInfo → res.data.result[0]) and updateOrgInfo's
  // setAuth merge. Fails soft: if the fetch returns nothing, the cached orgInfo is
  // left untouched (badge stays as-is until next reload), so a transient API error
  // never wipes the source of truth or throws into the caller's sign handler.
  // Monotonic guard for the refresh/patch race: a caller that patches orgInfo
  // directly via setAuth (e.g. SalesRepDrawer's post-save pricing cache write)
  // bumps the seq so any in-flight refresh that STARTED before the patch drops
  // its now-stale DoctorOffice snapshot instead of clobbering the patch.
  const orgInfoRefreshSeqRef = useRef(0);
  const invalidateInFlightOrgInfoRefresh = () => {
    orgInfoRefreshSeqRef.current += 1;
  };
  const refreshOrgInfo = async () => {
    const seqAtStart = orgInfoRefreshSeqRef.current;
    try {
      const res = await api.getOrgInfo();
      const orgInfo = res?.data?.result?.[0];
      if (orgInfo && orgInfoRefreshSeqRef.current === seqAtStart) {
        setAuth((prev) => (prev ? { ...prev, orgInfo } : prev));
      }
    } catch (e) {
      console.warn("refreshOrgInfo failed; orgInfo-derived UI may be stale until reload:", e?.message);
    }
  };

  const updateNoteTemplate = async (encounterType, noteTemplate, modes) => {
    if (!auth.user.custom.noteSettings) {
      auth.user.custom.noteSettings = {};
    }

    let editProfile = {
      custom: {
        noteSettings: auth.user.custom.noteSettings,
      },
    };

    editProfile.custom.noteSettings[encounterType] = {
      sections: noteTemplate,
    };

    editProfile.custom.noteSettings.default = modes;

    await api.updatePatient(editProfile);

    setAuth((prev) => {
      const updated = { ...prev };
      updated.user = { ...updated.user, custom: { ...updated.user.custom } };
      if (!updated.user.custom.noteSettings) updated.user.custom.noteSettings = {};
      updated.user.custom.noteSettings = { ...updated.user.custom.noteSettings };
      updated.user.custom.noteSettings[encounterType] = { sections: noteTemplate };
      updated.user.custom.noteSettings.default = modes;
      return updated;
    });
  };

  const updateNoteSections = async (encounterType, noteSections, modes) => {
    if (!auth.user.custom.noteSettings) {
      auth.user.custom.noteSettings = {};
    }

    let editProfile = {
      custom: {
        noteSettings: auth.user.custom.noteSettings || {},
        noteSections: auth.user.custom.noteSections || {},
      },
    };

    editProfile.custom.noteSections[encounterType] = noteSections;
    editProfile.custom.noteSections.default = noteSections.reduce(function (map, obj) {
      const existingData = editProfile.custom.noteSections.default?.[obj.variable] || {};
      map[obj.variable] = { ...existingData, exclude: obj.exclude };

      if (auth?.user?.custom?.noteLanguage && auth?.user?.custom.noteLanguage !== "en") {
        map[obj.variable]["title_" + auth.user.custom.noteLanguage] = obj.title;
      } else {
        map[obj.variable]["title"] = obj.title;
      }
      return map;
    }, {});
    editProfile.custom.noteSettings.default = modes;

    await api.updateProfile(editProfile);

    setAuth((prev) => {
      const updated = { ...prev };
      updated.user = { ...updated.user, custom: { ...updated.user.custom } };
      updated.user.custom.noteSections = editProfile.custom.noteSections;
      if (!updated.user.custom.noteSettings) updated.user.custom.noteSettings = {};
      updated.user.custom.noteSettings = { ...updated.user.custom.noteSettings };
      updated.user.custom.noteSettings.default = modes;
      return updated;
    });
  };

  const hasGroup = (group, ignoreAdmin) => {
    if (auth?.user.role === "administrator" && !ignoreAdmin) return true;
    return auth?.user?.groups?.indexOf(group) !== -1;
  };

  const hasPermission = (permission) => {
    if (auth?.user.role === "administrator") return true;
    return auth?.session?.permissions?.[permission] === "a";
  };

  const isUltimateOrganization = () => {
    return auth?.session?.currentOrganizationPlan === "ultimate";
  };

  const isPlanExhausted = (plan) => {
    const noteCapExceeded = plan.maxNotes !== -1 && plan.totalNotes >= plan.maxNotes;
    const trialExpired = plan.trialEndDate && new Date() >= new Date(plan.trialEndDate);
    return noteCapExceeded || trialExpired;
  };

  // The plan is tagged with the organization it was loaded for, and only exposed while that organization is
  // current, so switching organizations can never show or enforce the previous one's billing state.
  const currentUserPlan = userPlan && userPlan.organizationName === auth?.session?.currentOrganizationName ? userPlan : null;

  const checkUserPlan = async () => {
    if (isDemoRef.current) return true;
    // Admin sessions are never blocked by a customer org's note cap or trial
    // expiry. Covers admins who switch orgs mid-session without re-logging in.
    if (isAdminUserRef.current) return true;
    if (limitReached) {
      setShowModalUpgrade(true);
      return false;
    }

    try {
      let userPlanResponse = await api.getUserPlan();
      let plan = userPlanResponse.data?.output;

      if (plan) {
        setUserPlan({ ...plan, organizationName: auth?.session?.currentOrganizationName });

        if (isPlanExhausted(plan)) {
          setLimitReached(true);
          setShowModalUpgrade(true);
          return false;
        }
        setLimitReached(false);
      }
    } catch (error) {
      console.error("Cannot refresh user plan", error);
    }

    return true;
  };

  // Gate for starting a new encounter (New Encounter / Quick Start / Import). Uses the plan loaded
  // at login instead of a round-trip per click; the backend Check Plan in Note - Create is the real
  // enforcement. Same exemptions as checkUserPlan.
  const canCreateEncounter = () => {
    if (isDemoRef.current || isAdminUserRef.current) return true;
    if (limitReached || (currentUserPlan && isPlanExhausted(currentUserPlan))) {
      setShowModalUpgrade(true);
      return false;
    }
    return true;
  };

  const fetchDoctors = async () => {
    // Wrap in try/catch — staging has been intermittently returning 500 from
    // /web/organization-users?filter=groups=doctor and the unhandled rejection
    // surfaces as a full-screen uncaught-error overlay that blocks the rest
    // of the app. The doctors list is a non-critical convenience (used for
    // name lookups in chat/orders); when it fails we just keep the previous
    // value (or empty array) and log to the console for ops visibility. The
    // dependent UIs degrade gracefully (doctor name falls back to "—" or the
    // raw id) instead of breaking the whole page.
    try {
      let doctors = await api.listDoctors();
      setDoctors(doctors.data.users);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("fetchDoctors failed; doctor lookups will fall back:", err?.response?.status, err?.message);
    }
  };

  const getDoctorName = (id, showRole) => {
    let doctor = doctors.find((d) => d.id === id);
    let role = "";
    if (showRole && doctor) {
      role = " (" + getTypeUser(doctor) + ")";
    }
    return doctor ? doctor.lastName + " " + doctor.firstName + role : "";
  };

  const getTypeUser = (user) => {
    if (user.role === "supervisor") {
      return t("users.roles.supervisor");
    } else if (user.role === "user") {
      return t("users.roles.patient");
    } else if (user.role === "administrator") {
      return t("users.roles.administrator");
    } else if (user.role === "advanced") {
      if (user.groups.indexOf("doctor") !== -1) {
        return t("users.roles.provider");
      } else if (user.groups.indexOf("team-member") !== -1) {
        return t("users.roles.teamMember");
      } else if (user.groups.indexOf("sales-rep") !== -1) {
        return t("users.roles.salesRep");
      } else {
        return t("users.roles.supportStaff");
      }
    }
  };

  const fetchColumnsSmartSheet = async (cols) => {
    let columns = [];

    if (!cols) {
      cols = auth.user?.custom?.tableView;
    }

    if (cols) {
      columns = cols;
    } else {
      columns = Object.values(tableViewOrg).map((col) => {
        return {
          id: col.id,
          show: true,
        };
      });
    }

    let orgColumns = [...tableViewOrg];
    let res = await api.getSmartSheet();
    let customCols = [];
    if (res.status === 200) {
      customCols = res.data.result;
      for (let col of res.data.result?.reverse()) {
        if (!orgColumns.find((o) => o.id === col.id)) {
          orgColumns.push(col);
        }
      }
    }

    for (let col of orgColumns) {
      // Add col if id not present in columns
      if (!columns.find((c) => c.id === col.id)) {
        columns.push({
          id: col.id,
          show: true,
        });
      }
    }

    for (let col of columns) {
      // Remove cols not present in customCols or orgColumns
      if (!orgColumns.find((o) => o.id === col.id) && !customCols.find((o) => o.id === col.id)) {
        columns = columns.filter((c) => c.id !== col.id);
      }
    }

    const orgColumnsMap = {};
    for (let col of orgColumns) {
      orgColumnsMap[col.id] = col;
    }

    for (let i = 0; i < columns.length; i++) {
      const meta = orgColumnsMap[columns[i].id];
      if (meta) {
        columns[i] = {
          ...columns[i],
          label: meta.label || columns[i].label,
          type: meta.type || columns[i].type,
        };
      }
    }

    setColumnsSmartSheet([...columns]);
    let keys = columns.map((col) => {
      let colKey = tableViewData[col.id] || {
        id: col.id,
        header: col.label,
        key: "contextVariables.local:tableData_" + col.id,
        type: col.type || "text",
      };
      return { key: colKey.key, label: colKey.header, type: colKey.type };
    });

    setColumnsSmartSheetKeys(keys);
  };

  const requiredBaas = useMemo(() => {
    if (!auth?.user) {
      return [];
    }

    const shouldSignBaa =
      auth?.session?.currentOrganizationPlan === "order-provider" ||
      auth?.session?.currentOrganizationPlan === "order-manufacturer";

    if (!shouldSignBaa) {
      return [];
    }

    const isInvitedUser = !!auth?.user?.custom?.invitedBy;
    if (isInvitedUser) {
      return [];
    }

    const isDoctor = auth?.user?.role === "advanced" && auth?.user?.groups?.indexOf("doctor") !== -1;

    const isSalesRep = auth?.user?.groups?.indexOf("sales-rep") !== -1 || auth?.user?.groups?.indexOf("sales-rep-master") !== -1;

    const isClinicStaff = auth?.user?.role === "advanced" && !isDoctor && !isSalesRep;

    if (isClinicStaff) {
      return [];
    }

    const baseBaas = !isSalesRep ? ["lucas"] : [];

    const manufacturer = auth?.brain?.variables?.orderManufacturer;

    switch (manufacturer) {
      case "shoreline":
        return [...baseBaas, "shoreline", "meridian"];
      case "meridian":
        return [...baseBaas, "meridian"];
      case "gtm":
        return [...baseBaas, "gtm"];
      case "optimalcaregrp":
        return [...baseBaas, "optimalcaregrp"];
      default:
        return baseBaas;
    }
  }, [auth?.brain?.variables?.orderManufacturer, auth?.session?.currentOrganizationPlan, auth?.user]);

  const signedBaas = useMemo(() => {
    if (!auth?.user?.custom) {
      return [];
    }

    return Object.keys(baaDefinitions)
      .map((baa) => {
        const signedDate = auth.user.custom?.[`baaSignatureDate_${baa}`];

        if (!signedDate) {
          return null;
        }

        const signedVersion = auth.user.custom?.[`baaVersion_${baa}`];
        const definition = baaDefinitions[baa];

        return {
          id: baa,
          label: definition.title,
          signedDate,
          signedVersion,
        };
      })
      .filter(Boolean);
  }, [auth?.user?.custom]);

  const checkAutoLogin = async () => {
    let sessionId = sessionStorage.getItem("sessionId") || localStorage.getItem("sessionId");
    let autologin = Cookies.get("autologin");
    if (sessionId || autologin) {
      if (autologin) {
        Cookies.remove("autologin", { path: "/", domain: ".lucashealth.ai" });
        const autologinData = JSON.parse(autologin);
        // No trusted-device cookie is written here: the token is the server's, HttpOnly,
        // and host-only, so this host has to earn its own once. Writing the verification
        // code under that name only shadowed the real cookie -- see
        // clearLegacyTrustedDeviceCookies.

        login(
          autologinData.user,
          autologinData.password,
          autologinData.organization,
          undefined,
          autologinData.loginToken,
          autologinData.rememberLogin,
          autologinData.mode
        );
      } else {
        // Fire-and-forget on purpose (mount-time check, nothing to await into).
        // checkAuth rethrows on failure so its OTHER callers (revalidateSession,
        // login) can branch on success/failure — but here nothing awaits it, so
        // an expired/missing session (403, sessionExpired) became an unhandled
        // promise rejection on every visit with a stale sessionId in storage.
        // That is completely expected (not logged in yet), not a bug: it was
        // Sentry's single largest source of noise (83K events / 745 users over
        // 90 days). checkAuth already resets to a logged-out state before
        // rethrowing, so there is nothing left to do here except keep genuine,
        // unexpected failures visible.
        checkAuth(true).catch((error) => {
          if (!error?.sessionExpired) {
            console.error("checkAutoLogin: checkAuth failed", error);
          }
        });
      }
    }
  };

  // Chrome extension side panel: the panel relays the breadcrumb its
  // chrome.storage.session kept alive while the panel was closed (see
  // utils/extensionSessionBridge.js). Its own restore path rather than a branch of
  // checkAutoLogin, because it normally loses the race with it — Routes.js fires
  // checkAutoLogin on mount, and at that point sessionStorage is empty and the
  // handshake has not arrived, so it returns without ever asking the server.
  //
  // Gated on a validated chrome-extension: handshake, so nothing here is reachable
  // in a normal tab, in the native shell (top-level, no parent) or in a white-label
  // embed. restoreRelayedSession makes exactly one attempt and swallows the failure;
  // the bridge itself refuses to offer a second one.
  //
  // INVARIANT: at most one auth bootstrap per document load (council iter1 REMAND
  // fix). `isAuthenticated: !!auth` only catches the case where checkAutoLogin's
  // own checkAuth has already RESOLVED — a remember-me user reopening the panel, or
  // any full reload inside it, has a surviving sessionStorage/localStorage
  // breadcrumb that starts checkAutoLogin's bootstrap on mount, and the handshake
  // routinely arrives while that is still in flight (auth still null). Without a
  // second guard this effect would start a CONCURRENT duplicate bootstrap for the
  // same identity. restoreRelayedSession itself carries that guard (skips when
  // either storage already holds a breadcrumb) so it is enforced regardless of
  // which caller invokes it — see utils/extensionSessionBridge.js.
  useEffect(() => {
    return extensionSessionBridge.install((sessionId) => {
      restoreRelayedSession({ sessionId, isAuthenticated: !!auth, checkAuth });
    });
    // Re-installed on auth changes so the handler never calls a checkAuth captured
    // from an earlier render (same reason usePostMessages re-binds its listener).
    // eslint-disable-next-line
  }, [auth, isOnline]);

  return {
    checkAuth,
    logout,
    login,
    loginOffline,
    setAuth,
    isDemoRef,
    firstCheck,
    setFirstCheck,
    isMobileApp,
    auth,
    brainId,
    hasNotifications,
    setHasNotifications,
    termsOfUseVersion,
    baaDefinitions,
    requiredBaas,
    changeLocation,
    location,
    updateProfile,
    updateNoteTemplate,
    updateNoteSections,
    isMobile,
    isDoctor,
    isOnline,
    setIsOnline,
    serverReachable,
    isServerAvailable,
    brand,
    isOrdersBrand,
    hostName,
    hasGroup,
    hasPermission,
    isDebugMode,
    setIsDebugMode,
    showModalUpgrade,
    setShowModalUpgrade,
    userPlan: currentUserPlan,
    checkUserPlan,
    canCreateEncounter,
    isUltimateOrganization,
    doctors,
    getDoctorName,
    getTypeUser,
    firstLogin,
    isChromeExtension,
    showModalChangeLocation,
    setShowModalChangeLocation,
    isChromeExtensionInstalled,
    setIsChromeExtensionInstalled,
    checkLocation,
    isAdminUser,
    fetchColumnsSmartSheet,
    columnsSmartSheet,
    columnsSmartSheetKeys,
    signedBaas,
    home,
    setHome,
    showLeftSidebar,
    setShowLeftSidebar,
    iconSidebar,
    setIconSidebar,
    updateOrgInfo,
    updateOrgCustomNotes,
    updateUserCustomNotes,
    refreshOrgInfo,
    invalidateInFlightOrgInfoRefresh,
    checkAutoLogin,
  };
}

export function ProvideAuth({ children }) {
  const values = useAuthProvider();
  return <authContext.Provider value={values}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

const getBaseURL = () => {
  let brand = sessionStorage.getItem("brand");
  let appUrl = process.env.REACT_APP_ENDPOINT;
  if (brand === "healcore") appUrl = process.env.REACT_APP_ENDPOINT_HEALCORE;
  if (brand === "rpg") appUrl = process.env.REACT_APP_ENDPOINT_RPG;
  if (brand === "shoreline") appUrl = process.env.REACT_APP_ENDPOINT_SHORELINE;
  if (brand === "gtm") appUrl = process.env.REACT_APP_ENDPOINT_GTM;
  if (brand === "orders") appUrl = process.env.REACT_APP_ENDPOINT_ORDERS;
  if (brand === "freehealthai") appUrl = process.env.REACT_APP_ENDPOINT_FREEHEALTHAI;
  if (brand === "optimalcaregrp") appUrl = process.env.REACT_APP_ENDPOINT_OPTIMALCAREGRP;
  return appUrl;
};
